import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { useQuota } from "@/lib/quota-context";
import { getCheckoutStatus } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * Landing page after a Stripe Checkout completes (or is cancelled).
 *
 * URL shapes:
 *   /billing/return?session_id=cs_test_...   (success path from Stripe)
 *   /billing/return?cancelled=1              (cancel path)
 *
 * Why polling (and not webhook-only):
 * The integration playbook explicitly requires the UI to poll the
 * `/api/billing/checkout/status/{session_id}` endpoint until either
 *   - payment_status == "paid"          → granted
 *   - status == "expired"               → terminal failure
 *   - max attempts reached              → show "still pending, check email"
 *
 * Polling is the canonical credit path; the webhook is redundancy.
 */
const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

const BillingReturn = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const { refresh: refreshQuota } = useQuota();
  const [state, setState] = useState({ kind: "loading", message: t("billing.checking") });
  const pollsRef = useRef(0);

  useEffect(() => {
    if (params.get("cancelled")) {
      trackEvent("checkout_cancelled", {});
      setState({ kind: "cancelled", message: t("billing.cancelled") });
      return;
    }
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ kind: "error", message: t("billing.missingSession") });
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          // Sync the quota context so the next analyze call doesn't see stale state.
          refreshQuota();
          const isPremium = data.kind === "premium";
          // GA4 e-commerce conversion event. Value/currency map to the
          // Stripe checkout amount so GA4's "Monetization" reports work.
          trackEvent("purchase", {
            transaction_id: sessionId,
            value: isPremium ? 9.9 : 1.99,
            currency: "EUR",
            items: [
              {
                item_id: isPremium ? "premium_30d" : "credit_1",
                item_name: isPremium ? "Premium 30 days" : "Single report credit",
                price: isPremium ? 9.9 : 1.99,
                quantity: 1,
              },
            ],
          });
          if (isPremium) {
            trackEvent("subscription_started", { plan: "premium_30d" });
          }
          setState({
            kind: "success",
            message: isPremium ? t("billing.successPremium") : t("billing.successCredit"),
          });
          return;
        }
        if (data.status === "expired") {
          setState({ kind: "error", message: t("billing.expired") });
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setState({ kind: "pending", message: t("billing.stillPending") });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setState({
          kind: "error",
          message: typeof detail === "string" ? detail : t("billing.failed"),
        });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [params, refreshQuota, t]);

  const Icon =
    state.kind === "success"
      ? CheckCircle2
      : state.kind === "loading"
      ? Loader2
      : XCircle;
  const tone =
    state.kind === "success"
      ? "text-[#4A5D23]"
      : state.kind === "loading"
      ? "text-[#6C706D]"
      : "text-[#A0522D]";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div
        data-testid="billing-return"
        data-state={state.kind}
        className="bg-white border-2 border-[#282C2A] shadow-paper p-10 max-w-xl w-full text-center fade-up"
      >
        <Icon
          size={48}
          strokeWidth={1.5}
          className={`mx-auto ${tone} ${state.kind === "loading" ? "animate-spin" : ""}`}
        />
        <div className="label-eyebrow mt-6">{t("billing.eyebrow")}</div>
        <h1 className="font-serif-display text-3xl mt-3 text-[#282C2A]">{state.message}</h1>
        <Link
          to="/"
          data-testid="billing-back-home"
          className="inline-flex items-center gap-2 mt-8 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider bg-[#282C2A] text-white hover:bg-[#4A5D23] transition-colors"
        >
          {t("billing.backHome")}
        </Link>
      </div>
    </div>
  );
};

export default BillingReturn;
